import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";
// Components
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
// Pages
import Home from "./pages/Home";
import Careers from "./pages/Careers";
import Contact from "./pages/Contact";

// About Pages
import About from "./pages/about/About";
import OurStory from "./pages/about/OurStory";
import Philosophy from "./pages/about/Philosophy";
import Leadership from "./pages/about/Leadership";
import OurMilestones from "./pages/about/OurMilestones";
import Facilities from "./pages/about/Facilities";

// Business Pages
import BusinessAPIs from "./pages/business/APIs";
import BusinessCMO from "./pages/business/CMO";
import BusinessCosmetics from "./pages/business/Cosmetics";
import BusinessFormulations from "./pages/business/Formulations";
import BusinessIntermediates from "./pages/business/Intermediates";
import BusinessSpecialtyChemicals from "./pages/business/SpecialtyChemicals";

// Products Pages
import ProductAPIs from "./pages/products/APIs";
import ProductCosmetics from "./pages/products/Cosmetics";
import ProductFormulations from "./pages/products/Formulations";
import ProductIntermediates from "./pages/products/Intermediates";
import ProductSpecialtyChemicals from "./pages/products/SpecialtyChemicals";

// Navbar Component
function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const linkClasses = ({ isActive }) =>
    `transition px-3 py-2 rounded-md ${
      isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:text-blue-600"
    }`;

  return (
    <nav className="bg-white shadow-md fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/logo.jpg"
                alt="My Company Logo"
                className="h-14 sm:h-18 w-40 sm:w-48 object-contain"
              />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-6 items-center">
            <NavLink to="/" className={linkClasses}>
              Home
            </NavLink>

            {/* About Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setAboutOpen(true)}
              onMouseLeave={() => setAboutOpen(false)}
            >
              <button className="px-3 py-2 text-gray-700 hover:text-blue-600 flex items-center">
                About
                <svg
                  className={`w-4 h-4 ml-1 transform transition-transform ${
                    aboutOpen ? "rotate-180" : "rotate-0"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {aboutOpen && (
                <div className="absolute top-10 left-0 w-56 bg-white border rounded shadow-lg">
                  <NavLink to="/about/our-story" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Our Story
                  </NavLink>
                  <NavLink to="/about/philosophy" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Philosophy
                  </NavLink>
                  <NavLink to="/about/leadership" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Leadership
                  </NavLink>
                  <NavLink to="/about/our-milestones" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Our Milestones
                  </NavLink>
                  <NavLink to="/about/facilities" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Facilities
                  </NavLink>
                </div>
              )}
            </div>

            {/* Business Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setBusinessOpen(true)}
              onMouseLeave={() => setBusinessOpen(false)}
            >
              <button className="px-3 py-2 text-gray-700 hover:text-blue-600 flex items-center">
                Business
                <svg
                  className={`w-4 h-4 ml-1 transform transition-transform ${
                    businessOpen ? "rotate-180" : "rotate-0"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {businessOpen && (
                <div className="absolute top-10 left-0 w-56 bg-white border rounded shadow-lg">
                  <NavLink to="/business/cmo" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    CMO
                  </NavLink>
                  <NavLink to="/business/apis" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    APIs
                  </NavLink>
                  <NavLink to="/business/intermediates" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Intermediates
                  </NavLink>
                  <NavLink to="/business/specialty-chemicals" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Specialty Chemicals
                  </NavLink>
                  <NavLink to="/business/cosmetics" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Cosmetics
                  </NavLink>
                  <NavLink to="/business/formulations" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Formulations
                  </NavLink>
                </div>
              )}
            </div>

            {/* Products Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setProductsOpen(true)}
              onMouseLeave={() => setProductsOpen(false)}
            >
              <button className="px-3 py-2 text-gray-700 hover:text-blue-600 flex items-center">
                Products
                <svg
                  className={`w-4 h-4 ml-1 transform transition-transform ${
                    productsOpen ? "rotate-180" : "rotate-0"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {productsOpen && (
                <div className="absolute top-10 left-0 w-56 bg-white border rounded shadow-lg">
                  <NavLink to="/products/apis" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    APIs
                  </NavLink>
                  <NavLink to="/products/intermediates" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Intermediates
                  </NavLink>
                  <NavLink to="/products/specialty-chemicals" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Specialty Chemicals
                  </NavLink>
                  <NavLink to="/products/cosmetics" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Cosmetics
                  </NavLink>
                  <NavLink to="/products/formulations" className="block px-4 py-2 text-gray-700 hover:bg-blue-50">
                    Formulations
                  </NavLink>
                </div>
              )}
            </div>

            <NavLink to="/careers" className={linkClasses}>
              Careers
            </NavLink>
            <NavLink to="/contact" className={linkClasses}>
              Contact Us
            </NavLink>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={toggleMenu} className="text-gray-700 hover:text-blue-600">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        
        <div className="absolute top-16 left-0 w-full bg-white shadow-lg border-t border-gray-200 animate-slideDown z-40">
          <NavLink
      to="/"
      className="block px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
      onClick={() => setMenuOpen(false)}
    >
      Home
    </NavLink>
          {/* About Dropdown Mobile */}
          <div>
            <button
              onClick={() => setAboutOpen(!aboutOpen)}
              className="w-full text-left block px-6 py-3 text-gray-700 hover:bg-blue-50"
            >
              About
            </button>
            {aboutOpen && (
              <div className="pl-6">
                <NavLink to="/about/our-story" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Our Story
                </NavLink>
                <NavLink to="/about/philosophy" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Philosophy
                </NavLink>
                <NavLink to="/about/leadership" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Leadership
                </NavLink>
                <NavLink to="/about/our-milestones" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Our Milestones
                </NavLink>
                <NavLink to="/about/facilities" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Facilities
                </NavLink>
              </div>
            )}
          </div>

          {/* Business Dropdown Mobile */}
          <div>
            <button
              onClick={() => setBusinessOpen(!businessOpen)}
              className="w-full text-left block px-6 py-3 text-gray-700 hover:bg-blue-50"
            >
              Business
            </button>
            {businessOpen && (
              <div className="pl-6">
                <NavLink to="/business/cmo" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  CMO
                </NavLink>
                <NavLink to="/business/apis" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  APIs
                </NavLink>
                <NavLink to="/business/intermediates" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Intermediates
                </NavLink>
                <NavLink to="/business/specialty-chemicals" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Specialty Chemicals
                </NavLink>
                <NavLink to="/business/cosmetics" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Cosmetics
                </NavLink>
                <NavLink to="/business/formulations" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Formulations
                </NavLink>
              </div>
            )}
          </div>

          {/* Products Dropdown Mobile */}
          <div>
            <button
              onClick={() => setProductsOpen(!productsOpen)}
              className="w-full text-left block px-6 py-3 text-gray-700 hover:bg-blue-50"
            >
              Products
            </button>
            {productsOpen && (
              <div className="pl-6">
                <NavLink to="/products/apis" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  APIs
                </NavLink>
                <NavLink to="/products/intermediates" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Intermediates
                </NavLink>
                <NavLink to="/products/specialty-chemicals" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Specialty Chemicals
                </NavLink>
                <NavLink to="/products/cosmetics" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Cosmetics
                </NavLink>
                <NavLink to="/products/formulations" className="block px-6 py-2 text-gray-700 hover:bg-blue-50" onClick={() => setMenuOpen(false)}>
                  Formulations
                </NavLink>
              </div>
            )}
          </div>

          <NavLink to="/careers" className="block px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition" onClick={() => setMenuOpen(false)}>
            Careers
          </NavLink>
          <NavLink to="/contact" className="block px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition" onClick={() => setMenuOpen(false)}>
            Contact Us
          </NavLink>
        </div>
      )}
    </nav>
  );
}

// Main App Component
export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <Navbar />
      <div className="pt-16 min-h-screen flex flex-col">
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />

            {/* About Routes */}
            <Route path="/about" element={<About />} />
            <Route path="/about/our-story" element={<OurStory />} />
            <Route path="/about/philosophy" element={<Philosophy />} />
            <Route path="/about/leadership" element={<Leadership />} />
            <Route path="/about/our-milestones" element={<OurMilestones />} />
            <Route path="/about/facilities" element={<Facilities />} />

            {/* Business Routes */}
            <Route path="/business/apis" element={<BusinessAPIs />} />
            <Route path="/business/cmo" element={<BusinessCMO />} />
            <Route path="/business/cosmetics" element={<BusinessCosmetics />} />
            <Route path="/business/formulations" element={<BusinessFormulations />} />
            <Route path="/business/intermediates" element={<BusinessIntermediates />} />
            <Route path="/business/specialty-chemicals" element={<BusinessSpecialtyChemicals />} />

            {/* Products Routes */}
            <Route path="/products/apis" element={<ProductAPIs />} />
            <Route path="/products/cosmetics" element={<ProductCosmetics />} />
            <Route path="/products/formulations" element={<ProductFormulations />} />
            <Route path="/products/intermediates" element={<ProductIntermediates />} />
            <Route path="/products/specialty-chemicals" element={<ProductSpecialtyChemicals />} />

            {/* Other Pages */}
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
          </Routes>
        </div>
        <Footer />
      </div>
    </Router>
  );
}
